import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { hypervisorFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Hypervisor Memory Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['statusMemUtil', 'avgMem', 'vmConsumedB', 'vmActiveB', 'vmGrantedB', 'vmCapacityB'],
    },
};

const PERCENT = 'PERCENT';
const BYTES = 'BYTES';

const typeConfig = {
    [PERCENT]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (%)`,
                },
                ceiling: 100,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusMemUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.PERCENT,
                units: '%',
                chartType: 'spline',
                key: 'avgMem',
                label: t`Usage`,
                multiplier: 1,
                forceDecimals: true,
            },
        ],
    },
    [BYTES]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (Bytes)`,
                },
                ceiling: undefined,
            },
        ],
        tooltip: BuiltInTooltip,
        showGranularity: true,
        statusMetric: 'statusMemUtil',
        chartTrends: [
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmConsumedB',
                label: t`Usage`,
                multiplier: 1,
                forceDecimals: true,
            },
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmActiveB',
                label: t`Active`,
                multiplier: 1,
                forceDecimals: true,
            },
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmGrantedB',
                label: t`Granted`,
                multiplier: 1,
                forceDecimals: true,
            },
            {
                formatter: FORMATTER_TYPES.BYTE,
                chartType: 'spline',
                key: 'vmCapacityB',
                label: t`Capacity`,
                multiplier: 1,
                forceDecimals: true,
            },
        ],
    },
};

function useChartConfig(type) {
    const colorUsage = useMetricColor('serverCpu');
    const colorActive = useMetricColor('serverMemActive');
    const colorCapacity = useMetricColor('serverMemCapacity');
    const colorGranted = useMetricColor('serverMemGranted');

    return useMemo(() => {
        const config = typeConfig[type];
        config.chartTrends[0].color = colorUsage;
        if (type === BYTES) {
            config.chartTrends[1].color = colorActive;
            config.chartTrends[2].color = colorGranted;
            config.chartTrends[3].color = colorCapacity;
        }
        return config;
    }, [type, colorUsage, colorActive, colorCapacity, colorGranted]);
}

const selections = [
    { value: PERCENT, label: '%' },
    { value: BYTES, label: t`Bytes` },
];

function HypervisorMemoryUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

HypervisorMemoryUtilizationPanel.propTypes = panelPropTypes;

HypervisorMemoryUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareHostMemoryUtilTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.HYPERVISOR,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Memory utilization trend for a selected hypervisor`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, hypervisorFilterId],
        optional: [],
    },
})(HypervisorMemoryUtilizationPanel);
